import { useState } from "react";
import {
  Box,
  Typography,
  Button,
  ButtonGroup,
  FormLabel,
  FormControlLabel,
  IconButton,
  Tooltip,
  Grid,
  MenuItem,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Badge,
  useTheme
} from "@mui/material";
import { LocalizationProvider ,DateTimePicker} from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers-pro";

// ** Icon Imports
import Icon from "../../../components/icon";

// ** Custom Component Imports
import CustomTextField from "../../../components/mui/text-field";
import CustomChip from "../../../components/mui/chip";
import CustomAutocomplete from "components/mui/autocomplete";

// ** Third Party Imports
import isEqual from "lodash/isEqual";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const MenuProps = {
  PaperProps: {
    style: {
      width: 250,
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
    },
  },
};

const tagList = ["Senior", "Junior", "Intern", "On-call"];

const AdvancedFilter = ({onApplyFilters, handleReset, departments, filtered, hasEmployeeSearchPermission}) => {
  const theme = useTheme();

  // ** States 
  const [openEmpInfo, setOpenEmpInfo] = useState(false);
  const [openEmpDetail, setOpenEmpDetail] = useState(false);
  const [isAlwaysAvailable, setIsAlwaysAvailable] = useState(false);
  const [tags, setTags] = useState([]);
  const [openWageDetail, setOpenWageDetail] = useState(false);
  const [wageType, setWageType] = useState("select");
  const [openWorkRecord, setOpenWorkRecord] = useState(false);
  const [open, setOpen] = useState(false);
  const [assignToOpen, setAssignToOpen] = useState(false);
  const [jobTitleOpen, setJobTitleOpen] = useState(false);
  const [departmentOpen, setDepartmentOpen] = useState(false);
  const departmentArray = departments.map(dept => dept.value)

  const initialFilterState = {
    fullName: '',
    jobTitle: 'select',
    employeeRole: 'select',
    employeeType: 'select',
    phoneNumber: '',
    email: '',
    address: '',
    employmentStatus: 'select',
    department: 'select',
    hireDateFrom: null,
    hireDateTo: null,
    tags: [],
    wageType: 'select',
    hourlyRate: '',
    annualPay: '',
    minHoursWorked: '',
    maxHoursWorked: '',
    assignedTo: 'select',
    isAlwaysAvailable: false,
    availableFrom: null,
    availableTo: null,
  }
  const [filters, setFilters] = useState(initialFilterState);

  const handleApply = () => {
    if(!hasEmployeeSearchPermission) return;

    const sanitizedFilters = {
      ...filters,
      jobTitle: filters.jobTitle === "select" ? "" : filters.jobTitle,
      employeeRole: filters.employeeRole === "select" ? "" : filters.employeeRole,
      employeeType: filters.employeeType === "select" ? "" : filters.employeeType,
      employmentStatus: filters.employmentStatus === "select" ? "" : filters.employmentStatus,
      department: filters.department === "select" ? "" : filters.department,
      wageType: filters.wageType === "select" ? "" : filters.wageType,
      assignedTo: filters.assignedTo === "select" ? "" : filters.assignedTo,
    };

    onApplyFilters(sanitizedFilters); // Pass the filters to the parent
    setOpen(false)
  };

  const resetFilter = () => {
    handleReset();
    // setOpen(false);
    setFilters(initialFilterState);
    setWageType('select');
    setTags([]);

  }

  const handleWageTypeChange = (event) => {
    setFilters({ ...filters, wageType: event.target.value })
    setWageType(event.target.value);    
  };

  const handleTagChange = (event) => {
    const value = event.target.value;
    setTags(typeof value === "string" ? value.split(",") : value);
    setFilters({ ...filters, tags: (typeof value === "string" ? value.split(",") : value) })
  };

  const sections = {
    employeeInfo: ["fullName", "jobTitle", "employeeRole", "employeeType", "phoneNumber", "email", "address"],
    employeeDetails: ["employmentStatus", "department", "hireDateFrom", "hireDateTo", "tags", "isAlwaysAvailable", "availableFrom", "availableTo"],
    wages: ["wageType"],
    workRecords: ["minHoursWorked", "maxHoursWorked", "assignedTo"]
  };
  const getSelectedCount = (sectionKeys) => {
    return sectionKeys.filter((key) => {
      const value = filters[key];
      // Ignore falsy values, "select", and empty arrays
      if (Array.isArray(value)) {
        return value.length > 0; // Count only non-empty arrays
      }
      return value && value !== "select";
    }).length;
  };  

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {/* <Box> */}
        { isEqual(initialFilterState, filters) ? (
          <Button 
            variant='outlined' 
            color={theme.palette.mode === 'dark' ? 'secondary' : 'primary'}
            startIcon={<Icon icon='tabler:filter' />}
            disabled={!hasEmployeeSearchPermission}
            {...(hasEmployeeSearchPermission && {
              onClick: ()=> setOpen(true)
            })}
            sx={{
              width: { xs: '100%', sm: 'auto' }
            }}
          >Filter</Button>
        ) : (
          <Badge 
            color='error' 
            sx={{ cursor: 'pointer'}}
            badgeContent={
            <Typography variant='body2' sx={{ color:'white', lineHeight: 1 }} 
              onClick={resetFilter}
              overlap='circular'
            >
              X
            </Typography>
            } 
          >
            <Button 
              variant='outlined' 
              color={theme.palette.mode === 'dark' ? 'secondary' : 'primary'}
              startIcon={<Icon icon='tabler:filter' />}
              disabled={!hasEmployeeSearchPermission}
              {...(hasEmployeeSearchPermission && {
                onClick: ()=> setOpen(true)
              })}
              sx={{
                width: { xs: '100%', sm: 'auto' }
              }}
            >Filter</Button>
          </Badge>
        )}
        <Dialog
          fullWidth
          maxWidth="sm"
          open={open}
          onClose={() => setOpen(false)}
          aria-labelledby="form-dialog-title"
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "20px 24px",
            }}
          >
            <Typography variant="h3">Advanced Filter</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2">
                {filtered ? filtered.length : '0'} employees match your criteria
              </Typography>
              <Tooltip title='You don’t need to input exact details. A part of the name, address, or phone number is enough to proceed.'>
                <IconButton color='info'>
                  <Icon icon='tabler:info-circle' />
                </IconButton>
              </Tooltip>
            </Box>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ pointerEvents: !hasEmployeeSearchPermission ? 'none' : 'default' }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <List component="nav" aria-label="main mailbox">
                <ListItem
                  disablePadding
                  sx={{ borderBottom: "1px solid #E1E4EA" }}
                >
                  <ListItemButton onClick={() => setOpenEmpInfo(!openEmpInfo)}>
                    <Icon
                      icon={
                        openEmpInfo
                          ? "tabler:chevron-down"
                          : "tabler:chevron-right"
                      }
                    />
                    <ListItemText
                      sx={{ ml: 4 }}
                      primary={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                        >
                          <Typography variant="body1" fontWeight="bold">
                            Employee Information
                          </Typography>
                          <CustomChip 
                            label={getSelectedCount(sections.employeeInfo)}
                            skin='light'
                            color='success'
                          />
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                <Collapse in={openEmpInfo} timeout="auto" unmountOnExit>
                  <Grid container spacing={5} py={4} px={4}>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        label="Full Name"
                        placeholder="John Doe"
                        value={filters.fullName}
                        onChange={(e) => setFilters({ ...filters, fullName: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {/* <CustomTextField
                        fullWidth
                        select
                        label="Job Title"
                        defaultValue="select"
                        value={filters.jobTitle}
                        onChange={(e) => setFilters({ ...filters, jobTitle: e.target.value })}
                      >
                        <MenuItem value="select" disabled>
                          Select
                        </MenuItem>
                        <MenuItem  value='CEO'>CEO</MenuItem>
                        <MenuItem  value='Owner'>Owner</MenuItem>
                        <MenuItem  value='Accountant'>Accountant</MenuItem>
                        <MenuItem  value='Crew leader'>Crew leader</MenuItem>
                        <MenuItem  value='Foreman'>Foreman</MenuItem>
                        <MenuItem  value='Property manager'>Property manager</MenuItem>
                        <MenuItem  value='Accounts payable'>Accounts payable</MenuItem>
                        <MenuItem  value='On-site maintenance staff'>On-site maintenance staff</MenuItem>
                        <MenuItem  value='Others'>Others</MenuItem>
                      </CustomTextField> */}
                      <CustomAutocomplete
                        open={jobTitleOpen}
                        onClose={() => setJobTitleOpen(false)}
                        options={['CEO', 'Owner', 'Accountant', 'Crew Leader', 'Foreman', 'Property Manager', 'Accounts Payable', 'On-site Maintenance Staff', 'Other']}
                        id='autocomplete-custom'
                        onChange={(e) => setFilters({ ...filters, jobTitle: e.target.value })}
                        getOptionLabel={(option) => option || ''}
                        renderInput={
                          params => 
                            <CustomTextField 
                              {...params} 
                              fullWidth 
                              label="Job Title"
                              placeholder='Select' 
                              onChange={(e) => setFilters({ ...filters, jobTitle: e.target.value })}
                              value={filters.jobTitle}
                              onClick={
                                () => setJobTitleOpen(!jobTitleOpen)
                              }
                            />
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        select
                        label="Employee Role"
                        defaultValue="select"
                        value={filters.employeeRole}
                        onChange={(e) => setFilters({ ...filters, employeeRole: e.target.value })}
                      >
                        <MenuItem value="select" disabled>
                          Select
                        </MenuItem>
                        <MenuItem value='Owner'>Owner</MenuItem>
                        <MenuItem value='Admin'>Admin</MenuItem>
                        <MenuItem value='Manager'>Manager</MenuItem>
                        <MenuItem value='Crew'>Crew</MenuItem>
                      </CustomTextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        select
                        label="Employee Type"
                        defaultValue="select"
                        value={filters.employeeType}
                        onChange={(e) => setFilters({ ...filters, employeeType: e.target.value })}
                      >
                        <MenuItem value="select" disabled>
                          Select
                        </MenuItem>
                        <MenuItem value="Employees">
                          Employee
                        </MenuItem>
                        <MenuItem value="Sub Contractors" >
                          Subcontractor
                        </MenuItem>
                      </CustomTextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        label="Phone Number"
                        placeholder="000 000 0000"
                        value={filters.phoneNumber}
                        onChange={(e) => setFilters({ ...filters, phoneNumber: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        label="Email"
                        placeholder="john@example.com"
                        value={filters.email}
                        onChange={(e) => setFilters({ ...filters, email: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        label="Address"
                        placeholder="123 street, main road"
                        value={filters.address}
                        onChange={(e) => setFilters({ ...filters, address: e.target.value })}
                      />
                    </Grid>
                  </Grid>
                </Collapse>
              </List>
              <List component="nav" aria-label="main mailbox">
                <ListItem
                  disablePadding
                  sx={{ borderBottom: "1px solid #E1E4EA" }}
                >
                  <ListItemButton
                    onClick={() => setOpenEmpDetail(!openEmpDetail)}
                  >
                    <Icon
                      icon={
                        openEmpDetail
                          ? "tabler:chevron-down"
                          : "tabler:chevron-right"
                      }
                    />
                    <ListItemText
                      sx={{ ml: 4 }}
                      primary={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                        >
                          <Typography variant="body1" fontWeight="bold">
                            Employee Details
                          </Typography>
                          <CustomChip 
                            label={getSelectedCount(sections.employeeDetails)}
                            skin='light'
                            color='success'
                          />
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                <Collapse in={openEmpDetail} timeout="auto" unmountOnExit>
                  <Grid container spacing={5} py={4} px={4}>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        select
                        label="Employment Status"
                        defaultValue="select"
                        value={filters.employmentStatus}
                        onChange={(e) => setFilters({ ...filters, employmentStatus: e.target.value })}
                      >
                        <MenuItem value="select" disabled>
                          Select
                        </MenuItem>
                        <MenuItem value='FullTime'>Full Time</MenuItem>
                        <MenuItem value='PartTime'>Part Time</MenuItem>
                        <MenuItem value='Contract'>Contract</MenuItem>
                      </CustomTextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {/* <CustomTextField
                        fullWidth
                        select
                        label="Department"
                        defaultValue="select"
                        value={filters.department}
                        onChange={(e) => setFilters({ ...filters, department: e.target.value })}
                      >
                        <MenuItem value="select" disabled>
                          Select
                        </MenuItem>
                        {departments.map((department) => (
                          <MenuItem key={department.key} value={department.key}>
                            {department.value}
                          </MenuItem>
                        ))}
                      </CustomTextField> */}
                      <CustomAutocomplete
                        open={departmentOpen}
                        onClose={() => setDepartmentOpen(false)}
                        options={departmentArray}
                        id='autocomplete-custom'
                        onChange={(e) => setFilters({ ...filters, department: e.target.value })}
                        getOptionLabel={(option) => option || ''}
                        renderInput={
                          params => 
                            <CustomTextField 
                              {...params} 
                              fullWidth 
                              label="Department"
                              placeholder='Select' 
                              onChange={(e) => setFilters({ ...filters, department: e.target.value })}
                              value={filters.department}
                              onClick={
                                () => setDepartmentOpen(!departmentOpen)
                              }
                            />
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                      >
                        <FormLabel
                          sx={{
                            fontWeight: 400,
                            fontSize: "13px",
                            marginBottom: "4px",
                            color: "text.primary",
                            height: "100%",
                          }}
                        >
                          Hire Date
                        </FormLabel>
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 5
                          }}
                        >
                          <DatePicker
                            label="From"
                            slotProps={{
                              textField: {
                                size: "small",
                                fullWidth: true,
                              },
                            }}
                            value={filters.hireDateFrom}
                            onChange={(e) => setFilters({ ...filters, hireDateFrom: e })}
                          />
                          <DatePicker
                            label="To"
                            slotProps={{
                              textField: {
                                size: "small",
                                fullWidth: true,
                              },
                            }}
                            value={filters.hireDateTo}
                            onChange={(e) => setFilters({ ...filters, hireDateTo: e })}
                          />
                        </Box>
                      </Box>
                    </Grid>
                   <Grid item xs={12}>
                    <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                    >
                    <FormLabel
                      sx={{
                        fontWeight: 400,
                        fontSize: "13px",
                        marginBottom: "4px",
                        color: "text.primary",
                      }}
                    >
                      Employee Availability
                    </FormLabel>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 2
                      }}
                    >
                      <FormControlLabel
                        sx={{ whiteSpace: "nowrap" }}
                        label="Always Available"
                        control={
                          <Checkbox
                            color="success"
                            checked={isAlwaysAvailable}
                            onChange={() => {
                              setIsAlwaysAvailable(!isAlwaysAvailable);
                              setFilters({ 
                                ...filters, 
                                isAlwaysAvailable: !isAlwaysAvailable,
                                // Clear date/time values when "Always Available" is checked
                                ...(isAlwaysAvailable ? {} : {
                                  availableFrom: null,
                                  availableTo: null
                                })
                              })
                            }}
                          />
                        }
                      />
                    </Box>

                    {!isAlwaysAvailable && (
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Typography variant="body1" color="text.secondary">
                              From
                            </Typography>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DateTimePicker
                               desktopModeMediaQuery="@media (max-width: 0px)" 
                                value={filters.availableFrom}
                                onChange={(e) => setFilters({ ...filters, availableFrom: e })}
                                slotProps={{
                                  textField: {
                                    size: "small",
                                    fullWidth: true,
                                  },
                                }}
                              />
                            </LocalizationProvider>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Typography variant="body1" color="text.secondary">
                              To
                            </Typography>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DateTimePicker
                               desktopModeMediaQuery="@media (max-width: 0px)" 
                                value={filters.availableTo}
                                onChange={(e) => setFilters({ ...filters, availableTo: e })}
                                slotProps={{
                                  textField: {
                                    size: "small",
                                    fullWidth: true,
                                  },
                                }}
                              />
                            </LocalizationProvider>
                          </Box>
                        </Grid>
                      </Grid>
                    )}
                    </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <CustomTextField
                        fullWidth
                        label="Tags"
                        placeholder="e.g., On-call, Senior, Junior"
                        value={filters.tags}
                        onChange={(e) => {
                          const tagString = e.target.value;
                          const tagArray = tagString.split(',').map(tag => tag.trim()).filter(tag => tag !== '');
                          setTags(tagArray);
                          setFilters({ ...filters, tags: tagArray });
                        }}
                      />
                    </Grid>
                  </Grid>
                </Collapse>
              </List>
              <List component="nav" aria-label="main mailbox">
                <ListItem
                  disablePadding
                  sx={{ borderBottom: "1px solid #E1E4EA" }}
                >
                  <ListItemButton
                    onClick={() => setOpenWageDetail(!openWageDetail)}
                  >
                    <Icon
                      icon={
                        openWageDetail
                          ? "tabler:chevron-down"
                          : "tabler:chevron-right"
                      }
                    />
                    <ListItemText
                      sx={{ ml: 4 }}
                      primary={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                        >
                          <Typography variant="body1" fontWeight="bold">
                            Wage and Pay Details
                          </Typography>
                          <CustomChip 
                            label={getSelectedCount(sections.wages)}
                            skin='light'
                            color='success'
                          />
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                <Collapse in={openWageDetail} timeout="auto" unmountOnExit>
                  <Grid container spacing={5} py={4} px={4}>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        select
                        label="Wage Type"
                        value={wageType}
                        onChange={handleWageTypeChange}
                      >
                        <MenuItem value="select" disabled>
                          Select
                        </MenuItem>
                        <MenuItem value="hourly">Hourly</MenuItem>
                        <MenuItem value="salary">Salary</MenuItem>
                      </CustomTextField>
                    </Grid>
                    {wageType === "hourly" && (
                      <Grid item xs={12} sm={6}>
                        <CustomTextField
                          fullWidth
                          label="Hourly Rate"
                          placeholder="$0.00"
                          value={filters.hourlyRate}
                          onChange={(e) => setFilters({ ...filters, hourlyRate: e.target.value })}
                        />
                      </Grid>
                    )}
                    {wageType === "salary" && (
                      <Grid item xs={12} sm={6}>
                        <CustomTextField
                          fullWidth
                          label="Annual Pay"
                          placeholder="$123,456"
                          value={filters.annualPay}
                          onChange={(e) => setFilters({ ...filters, annualPay: e.target.value })}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Collapse>
              </List>
              <List component="nav" aria-label="main mailbox">
                <ListItem
                  disablePadding
                  sx={{ borderBottom: "1px solid #E1E4EA" }}
                >
                  <ListItemButton
                    onClick={() => setOpenWorkRecord(!openWorkRecord)}
                  >
                    <Icon
                      icon={
                        openWorkRecord
                          ? "tabler:chevron-down"
                          : "tabler:chevron-right"
                      }
                    />
                    <ListItemText
                      sx={{ ml: 4 }}
                      primary={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                        >
                          <Typography variant="body1" fontWeight="bold">
                            Work Records
                          </Typography>
                          <CustomChip 
                            label={getSelectedCount(sections.workRecords)}
                            skin='light'
                            color='success'
                          />
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                <Collapse in={openWorkRecord} timeout="auto" unmountOnExit>
                  <Grid container spacing={5} py={4} px={4}>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        label="Minimum Hours Worked"
                        placeholder="0"
                        value={filters.minHoursWorked}
                        onChange={(e) => setFilters({ ...filters, minHoursWorked: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        label="Maximum Hours Worked"
                        placeholder="0"
                        value={filters.maxHoursWorked}
                        onChange={(e) => setFilters({ ...filters, maxHoursWorked: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {/* <CustomTextField
                        fullWidth
                        select
                        label="Assigned To"
                        defaultValue="select"
                        value={filters.assignedTo}
                        onChange={(e) => setFilters({ ...filters, assignedTo: e.target.value })}
                      >
                        <MenuItem value="select">Select</MenuItem>
                      </CustomTextField> */}
                      <CustomAutocomplete
                        open={assignToOpen}
                        onClose={() => setAssignToOpen(false)}
                        options={[]}
                        id='autocomplete-custom'
                        onChange={(e) => setFilters({ ...filters, assignedTo: e.target.value })}
                        getOptionLabel={(option) => option || ''}
                        renderInput={
                          params => 
                            <CustomTextField 
                              {...params} 
                              fullWidth 
                              label="Assigned To"
                              placeholder='Select' 
                              onChange={(e) => setFilters({ ...filters, assignedTo: e.target.value })}
                              value={filters.assignedTo}
                              onClick={
                                () => setAssignToOpen(!assignToOpen)
                              }
                            />
                        }
                      />
                    </Grid>
                  </Grid>
                </Collapse>
              </List>
            </Box>
          </DialogContent>
          <Divider />
          <DialogActions
            sx={{
              display: "flex",
              justifyContent: "space-between",
              padding: "20px 24px !important",
            }}
            className="dialog-actions-dense"
          >
            <Box>
              <Button
                variant="contained"
                color="info"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 4,
              }}
            >
              <Button variant="tonal" color="info"
                onClick={resetFilter}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                color="main"
                // onClick={() => setOpen(false)}
                onClick={handleApply}
              >
                Apply
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      {/* </Box> */}
    </LocalizationProvider>
  );
};

export default AdvancedFilter;
